import type { FivetranConnection, FivetranConnectionResponse } from '@/models/connection';
import { graphql } from './graphql';
import { assertIsDefined } from './typeChecks';

const INTEGRATION_ICONS_BY_CONNECTION_ID = 'beem-integration-meta-by-connection-id';

type IntegrationMeta = {
  icon: string;
  name: string;
};

export async function preloadAndCacheIntegrationMeta(
  connectionIds: string[],
  organizationId: string,
): Promise<void> {
  let reloadRequired = false;
  const cachedJson = localStorage.getItem(INTEGRATION_ICONS_BY_CONNECTION_ID);
  if (cachedJson) {
    const cachedIconsByConnectionId = JSON.parse(cachedJson);
    reloadRequired = connectionIds.some((el) => !cachedIconsByConnectionId[el]);
  } else {
    reloadRequired = true;
  }
  if (reloadRequired) {
    const iconsByConnectionId: Record<string, IntegrationMeta> = {};

    const rs1 = await graphql('listConnectionsByOrganizationFivetran', { organizationId });
    const { items: items1 } = rs1;
    if (items1.length > 0) {
      const fivetranIconUrlsByServiceName: Record<string, string> = {};
      const { response: rstr } = await graphql('fivetranFetchConnections', { query: {} });
      const response = JSON.parse(rstr) as FivetranConnectionResponse<FivetranConnection>;
      response.data.items.forEach((el) => {
        fivetranIconUrlsByServiceName[el.id] = el.icon_url;
      });
      type FivetranItem = {
        id: string;
        fivetranConnection?: { serviceName: string; integrationName: string };
      };
      await Promise.all(
        (items1 as FivetranItem[]).map(async ({ id, fivetranConnection }) => {
          assertIsDefined(fivetranConnection, 'BEEM231109160553');
          iconsByConnectionId[id] = {
            icon: fivetranIconUrlsByServiceName[fivetranConnection.serviceName] ?? '',
            name: fivetranConnection.integrationName,
          };
        }),
      );
    }

    const rs2 = await graphql('listConnectionsByOrganizationNative', { organizationId });
    const { items: items2 } = rs2;
    if (items2.length > 0) {
      type NativeItem = { id: string; integration?: { name?: string } };
      await Promise.all(
        (items2 as NativeItem[]).map(async ({ id, integration }) => {
          assertIsDefined(integration, 'BEEM231109160554');
          iconsByConnectionId[id] = {
            icon: integration.name ?? '',
            name: integration.name ?? '',
          };
        }),
      );
    }

    localStorage.setItem(INTEGRATION_ICONS_BY_CONNECTION_ID, JSON.stringify(iconsByConnectionId));
  }
}

export function getIntegrationMetaFromCache(connectionId: string): IntegrationMeta {
  const cachedJson = localStorage.getItem(INTEGRATION_ICONS_BY_CONNECTION_ID);
  if (!cachedJson) throw new Error('BEEM231108210757');
  const cache = JSON.parse(cachedJson) as Record<string, IntegrationMeta>;
  if (!cache[connectionId]) throw new Error('BEEM231108210758');
  return cache[connectionId];
}
